import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, UploadCloud, ShieldCheck } from "lucide-react";

export function AddToBrevoDialog({ open, onOpenChange, contactIds = [], leadIds = [], onDone }) {
  const total = contactIds.length + leadIds.length;
  const [listId, setListId] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: overview } = useQuery({
    queryKey: ["brevo-overview"],
    queryFn: async () => (await api.get("/marketing/brevo/overview")).data,
    enabled: open,
  });
  const lists = overview?.lists || [];

  const push = async () => {
    if (!listId) { toast.error("Pick a Brevo list"); return; }
    setBusy(true);
    try {
      const list = lists.find((l) => String(l.id) === String(listId));
      const { data } = await api.post("/marketing/brevo/push-contacts", {
        contact_ids: contactIds, lead_ids: leadIds, list_id: Number(listId), list_name: list?.name || "",
      });
      let msg = `${data.pushed} contact(s) added to "${list?.name}"`;
      if (data.skipped_no_email) msg += ` · ${data.skipped_no_email} skipped (no email)`;
      if (data.skipped_opted_out) msg += ` · ${data.skipped_opted_out} skipped (opted out)`;
      toast.success(msg);
      onOpenChange(false);
      setListId("");
      onDone?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" data-testid="add-to-brevo-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Add to Brevo list</DialogTitle>
          <DialogDescription>{total} selected {leadIds.length && !contactIds.length ? "lead(s)" : "contact(s)"} will be copied one-way into Brevo. Existing Brevo contacts are updated — never duplicated.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label className="text-xs">Brevo list *</Label>
          <Select value={listId} onValueChange={setListId}>
            <SelectTrigger className="h-9" data-testid="add-to-brevo-list-select"><SelectValue placeholder="Pick a Brevo list" /></SelectTrigger>
            <SelectContent>{lists.map((l) => <SelectItem key={l.id} value={String(l.id)}>{l.name} · {l.unique || l.total} contacts</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <p className="text-[11px] text-emerald-700 flex items-start gap-1.5"><ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          Contacts without an email or on the do-not-email list are skipped automatically.</p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={push} disabled={busy || !listId} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="add-to-brevo-confirm-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Add to Brevo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
